import { HardDrive } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useStorageStats } from '@/hooks/useStorageStats';

/**
 * Format bytes to human-readable format
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes === Infinity) return 'Unlimited';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

export function StorageUsageFooter() {
  const { data: storage, isLoading, error } = useStorageStats();

  // Show placeholder during loading
  if (isLoading) {
    return (
      <div className="flex-shrink-0 border-t border-border bg-background-subtle px-4 py-3">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-foreground-muted animate-pulse" />
          <span className="text-sm text-foreground-muted">Loading storage info...</span>
        </div>
      </div>
    );
  }

  // Don't show on error (fail silently)
  if (error || !storage) {
    return null;
  }

  const { usedBytes, limitBytes } = storage;
  const isUnlimited = limitBytes === Infinity;

  return (
    <div className="flex-shrink-0 border-t border-border bg-background-subtle px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        {/* Left side: Storage indicator */}
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-foreground-muted" />
          <span className="text-sm font-medium text-foreground">
            {isUnlimited
              ? `${formatBytes(usedBytes)} used`
              : `${formatBytes(usedBytes)} of ${formatBytes(limitBytes)} used`}
          </span>
        </div>

        {/* Right side: Upgrade link (only show if not unlimited) */}
        {!isUnlimited && (
          <Link
            to="/settings/subscription"
            className="text-sm font-medium text-primary hover:text-primary-hover"
          >
            Get more storage
          </Link>
        )}
      </div>
    </div>
  );
}
